/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package forecasting

/**
 * Forecasting method data models for UI method selection
 * Provides structured approach to algorithm selection and configuration
 */

/**
 * Categorization of forecasting methods for UI organization
 */
enum class ForecastingCategory {
    MOVING_AVERAGE,
    EXPONENTIAL_SMOOTHING,
    TREND_ANALYSIS,
    CUSTOM
}

/**
 * Definition of a forecasting method for UI selection
 */
data class ForecastingMethod(
    val id: String,
    val name: String,
    val algorithmName: String,
    val displayName: String,
    val description: String,
    val parameters: Map<String, Any>,
    val category: ForecastingCategory,
    val isEnabled: Boolean
)

/**
 * Method recommendation based on data analysis
 */
data class ForecastingMethodRecommendation(
    val methodId: String,
    val confidence: Double,
    val reason: String,
    val dataCharacteristics: Map<String, Any> = emptyMap()
)

/**
 * Selector for forecasting methods with intelligent recommendations
 */
class ForecastingMethodSelector(
    private val customMethods: List<ForecastingMethod> = emptyList()
) {
    
    private val availableMethods = mutableListOf<ForecastingMethod>()
    private var currentMethodId: String? = null
    
    init {
        initializeDefaultMethods()
        availableMethods.addAll(customMethods)
        currentMethodId = availableMethods.firstOrNull { it.isEnabled }?.id
    }
    
    /**
     * Get all available forecasting methods
     */
    fun getAvailableMethods(): List<ForecastingMethod> = availableMethods.toList()
    
    /**
     * Get current selected method
     */
    fun getCurrentMethod(): ForecastingMethod {
        val currentId = currentMethodId
        return availableMethods.find { it.id == currentId && it.isEnabled }
            ?: getDefaultMethod()
    }
    
    /**
     * Select a method by ID
     */
    fun selectMethod(methodId: String): Boolean {
        val method = availableMethods.find { it.id == methodId }
        return if (method != null && method.isEnabled) {
            currentMethodId = methodId
            true
        } else {
            false
        }
    }
    
    /**
     * Get methods by category
     */
    fun getMethodsByCategory(category: ForecastingCategory): List<ForecastingMethod> {
        return availableMethods.filter { it.category == category && it.isEnabled }
    }
    
    /**
     * Get enabled methods only
     */
    fun getEnabledMethods(): List<ForecastingMethod> {
        return availableMethods.filter { it.isEnabled }
    }
    
    private fun getDefaultMethod(): ForecastingMethod {
        return availableMethods.find { it.isEnabled } ?: createFallbackMethod()
    }
    
    private fun createFallbackMethod(): ForecastingMethod {
        return ForecastingMethod(
            id = "fallback_sma",
            name = "Simple Moving Average",
            algorithmName = "SimpleMovingAverage",
            displayName = "Moving Average",
            description = "Simple moving average fallback",
            parameters = mapOf("period" to 5),
            category = ForecastingCategory.MOVING_AVERAGE,
            isEnabled = true
        )
    }
    
    private fun initializeDefaultMethods() {
        availableMethods.addAll(listOf(
            ForecastingMethod(
                id = "sma_3",
                name = "3-Period Simple Moving Average",
                algorithmName = "SimpleMovingAverage",
                displayName = "SMA (3)",
                description = "Short-term smoothing over 3 periods",
                parameters = mapOf("period" to 3),
                category = ForecastingCategory.MOVING_AVERAGE,
                isEnabled = true
            ),
            ForecastingMethod(
                id = "sma_5",
                name = "5-Period Simple Moving Average",
                algorithmName = "SimpleMovingAverage",
                displayName = "SMA (5)",
                description = "Medium-term smoothing over 5 periods",
                parameters = mapOf("period" to 5),
                category = ForecastingCategory.MOVING_AVERAGE,
                isEnabled = true
            ),
            ForecastingMethod(
                id = "ewma_03",
                name = "Exponential Weighted Moving Average",
                algorithmName = "ExponentialWeightedMovingAverage",
                displayName = "EWMA (α=0.6)",
                description = "Exponentially weighted with alpha 0.6",
                parameters = mapOf("alpha" to 0.6),
                category = ForecastingCategory.MOVING_AVERAGE,
                isEnabled = true
            ),
            ForecastingMethod(
                id = "ses_06",
                name = "Simple Exponential Smoothing",
                algorithmName = "SimpleExponentialSmoothing",
                displayName = "SES (α=0.6)",
                description = "Simple exponential smoothing with alpha 0.6",
                parameters = mapOf("alpha" to 0.6),
                category = ForecastingCategory.EXPONENTIAL_SMOOTHING,
                isEnabled = true
            ),
            ForecastingMethod(
                id = "ltr_default",
                name = "Linear Trend Regression",
                algorithmName = "LinearTrendRegression",
                displayName = "Linear Trend",
                description = "Linear regression trend analysis",
                parameters = emptyMap(),
                category = ForecastingCategory.TREND_ANALYSIS,
                isEnabled = true
            )
        ))
    }
}

/**
 * Configuration manager for forecasting methods
 */
class ForecastingMethodConfiguration {
    
    private val methodSettings = mutableMapOf<String, Boolean>()
    private val methodParameters = mutableMapOf<String, Map<String, Any>>()
    private val defaultMethod = getDefaultMethod()
    
    /**
     * Get default method
     */
    fun getDefaultMethod(): ForecastingMethod {
        return ForecastingMethod(
            id = "default_sma",
            name = "Default Simple Moving Average",
            algorithmName = "SimpleMovingAverage",
            displayName = "Default SMA",
            description = "Default moving average method",
            parameters = mapOf("period" to 5),
            category = ForecastingCategory.MOVING_AVERAGE,
            isEnabled = true
        )
    }
    
    /**
     * Check if method is enabled
     */
    fun isMethodEnabled(methodId: String): Boolean {
        return methodSettings[methodId] ?: true // Default to enabled
    }
    
    /**
     * Enable a method
     */
    fun enableMethod(methodId: String) {
        methodSettings[methodId] = true
    }
    
    /**
     * Disable a method
     */
    fun disableMethod(methodId: String) {
        methodSettings[methodId] = false
    }
    
    /**
     * Set method parameters
     */
    fun setMethodParameters(methodId: String, parameters: Map<String, Any>) {
        methodParameters[methodId] = parameters
    }
    
    /**
     * Get method parameters
     */
    fun getMethodParameters(methodId: String): Map<String, Any>? {
        return methodParameters[methodId]
    }
    
    /**
     * Get all configured methods
     */
    fun getConfiguredMethods(): List<String> {
        return methodSettings.keys.toList()
    }
}

/**
 * Recommender for forecasting methods based on data characteristics
 */
class ForecastingMethodRecommender {
    
    private val baseService = ForecastingService()
    
    /**
     * Recommend method based on data characteristics
     */
    fun recommendMethodForData(data: List<Double>): ForecastingMethodRecommendation {
        if (data.isEmpty()) {
            return ForecastingMethodRecommendation(
                methodId = "sma_5",
                confidence = 0.1,
                reason = "Default choice for empty data"
            )
        }
        
        // Use base service algorithm recommendation
        val algorithmRecommendation = baseService.recommendAlgorithm(data)
        
        // Map algorithm to method ID
        val methodId = mapAlgorithmToMethodId(algorithmRecommendation.algorithmName)
        
        return ForecastingMethodRecommendation(
            methodId = methodId,
            confidence = algorithmRecommendation.confidence,
            reason = algorithmRecommendation.reason,
            dataCharacteristics = analyzeDataCharacteristics(data)
        )
    }
    
    private fun mapAlgorithmToMethodId(algorithmName: String): String {
        return when (algorithmName) {
            "SimpleMovingAverage" -> "sma_5"
            "ExponentialWeightedMovingAverage" -> "ewma_03"
            "SimpleExponentialSmoothing" -> "ses_06"
            "LinearTrendRegression" -> "ltr_default"
            else -> "sma_5"
        }
    }
    
    private fun analyzeDataCharacteristics(data: List<Double>): Map<String, Any> {
        if (data.isEmpty()) return emptyMap()
        
        val mean = data.average()
        val variance = data.map { (it - mean) * (it - mean) }.average()
        val trend = if (data.size > 1) data.last() - data.first() else 0.0
        
        return mapOf(
            "mean" to mean,
            "variance" to variance,
            "trend" to trend,
            "size" to data.size,
            "min" to (data.minOrNull() ?: 0.0),
            "max" to (data.maxOrNull() ?: 0.0)
        )
    }
}

/**
 * UI Model for method selector component
 */
class ForecastingMethodSelectorUIModel(
    private val selector: ForecastingMethodSelector = ForecastingMethodSelector()
) {
    
    private var currentFilter: ForecastingCategory? = null
    private var hasChanged = false
    private val initialSelection = selector.getCurrentMethod().id
    
    /**
     * Get methods for display
     */
    fun getDisplayMethods(): List<ForecastingMethod> {
        return if (currentFilter != null) {
            selector.getMethodsByCategory(currentFilter!!)
        } else {
            selector.getEnabledMethods()
        }
    }
    
    /**
     * Get current selection
     */
    fun getCurrentSelection(): ForecastingMethod {
        return selector.getCurrentMethod()
    }
    
    /**
     * Select method by ID
     */
    fun selectMethod(methodId: String): Boolean {
        val success = selector.selectMethod(methodId)
        if (success) {
            hasChanged = (methodId != initialSelection)
        }
        return success
    }
    
    /**
     * Filter by category
     */
    fun filterByCategory(category: ForecastingCategory) {
        currentFilter = category
    }
    
    /**
     * Clear filter
     */
    fun clearFilter() {
        currentFilter = null
    }
    
    /**
     * Check if selection has changed
     */
    fun hasSelectionChanged(): Boolean = hasChanged
    
    /**
     * Get available categories
     */
    fun getAvailableCategories(): List<ForecastingCategory> {
        return selector.getEnabledMethods()
            .map { it.category }
            .distinct()
            .sortedBy { it.ordinal }
    }
}

/**
 * Persistence layer for method selection
 */
class ForecastingMethodPersistence {
    
    private val storage = mutableMapOf<String, Any>()
    
    /**
     * Save selected method
     */
    fun saveSelectedMethod(methodId: String) {
        storage["selected_method"] = methodId
    }
    
    /**
     * Load selected method
     */
    fun loadSelectedMethod(): String? {
        return storage["selected_method"] as? String
    }
    
    /**
     * Save method configuration
     */
    fun saveMethodConfiguration(methodId: String, parameters: Map<String, Any>) {
        storage["config_$methodId"] = parameters
    }
    
    /**
     * Load method configuration
     */
    fun loadMethodConfiguration(methodId: String): Map<String, Any>? {
        return storage["config_$methodId"] as? Map<String, Any>
    }
    
    /**
     * Clear all stored data
     */
    fun clearAll() {
        storage.clear()
    }
}